import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
    ImpersonationInterceptor,
    AdditionalRoleInterceptor,
    ImpersonatedHttpClient,
    ApiKeyInterceptor
} from '@ceres/shared/services';
import { ENVIRONMENT } from '@ceres/frontend-helper';
import { BryntumSchedulerProModule } from '@bryntum/schedulerpro-angular';
import { NgxsModule } from '@ngxs/store';
import { environment } from '../environments/environment';
import { HomeModule } from './home/home.module';
import { FeaturesModule } from './features/features.module';
import { SharedModule } from './shared/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateRootModule } from './translate-root.module';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';
import { NewReleaseDialogComponent } from './release-notes/components/new-release-dialog/new-release-dialog.component';
import { ReleaseNotesState } from './release-notes/state/release-notes.state';

import { InteractionType, PublicClientApplication } from '@azure/msal-browser';

import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalGuardConfiguration,
    MsalInterceptor,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService
} from '@azure/msal-angular';

import { UnauthorizedInterceptor } from './auth/services/unauthorized-interceptor.service';
import { SafeMsalInterceptor } from './auth/services/safe-msal-interceptor.service';
import { of, firstValueFrom } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { InteractionStatus } from '@azure/msal-browser';

const msalInstanceFactory = () => new PublicClientApplication(environment.msalConfig);

const msalInterceptorConfigFactory = (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    for (const item of environment.apiConfig) {
        const uri = item.uri.includes('*') ? item.uri : `${item.uri.replace(/\/+$/, '')}/*`;
        protectedResourceMap.set(uri, item.scopes);
    }

    return {
        interactionType: InteractionType.Redirect,
        protectedResourceMap
    };
};

const msalGuardConfigFactory = (): MsalGuardConfiguration => ({
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: [...environment.apiConfig[1].scopes] },
    loginFailedRoute: '/unauthorized'
});

function initializeMsal(msalService: MsalService, msalBroadcast: MsalBroadcastService) {
    return () =>
        firstValueFrom(
            msalService.handleRedirectObservable().pipe(
                switchMap(() => msalBroadcast.inProgress$),
                filter((status) => status === InteractionStatus.None),
                take(1),
                map(() => {
                    const accounts = msalService.instance.getAllAccounts();

                    if (accounts.length > 0) {
                        const activeAccount = msalService.instance.getActiveAccount() || accounts[0];
                        msalService.instance.setActiveAccount(activeAccount);
                    }
                    return undefined;
                }),
                catchError(() => of(undefined))
            )
        );
}

@NgModule({
    imports: [
        BrowserModule,
        HttpClientModule,
        AppRoutingModule,
        TranslateRootModule,
        TranslatePipe,
        MatDialogModule,
        MatButtonModule,
        HomeModule,
        SharedModule.forRoot(),
        FeaturesModule.forRoot(),
        MsalModule,
        BryntumSchedulerProModule,
        NgxsModule.forRoot([ReleaseNotesState], {
            developmentMode: !environment.production,
            compatibility: { strictContentSecurityPolicy: true }
        })
    ],
    providers: [
        { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },

        MsalService,
        MsalGuard,
        MsalBroadcastService,

        // MsalInterceptor is provided so SafeMsalInterceptor can inject it
        MsalInterceptor,
        // SafeMsalInterceptor wraps MsalInterceptor to prevent token acquisition when no accounts exist
        // This prevents the interceptor from triggering a redirect that conflicts with MsalGuard
        { provide: HTTP_INTERCEPTORS, useClass: SafeMsalInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ApiKeyInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: AdditionalRoleInterceptor, multi: true },

        ImpersonationInterceptor,
        ImpersonatedHttpClient,

        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: ENVIRONMENT, useValue: environment },

        provideAppInitializer(() => {
            const fn = initializeMsal(inject(MsalService), inject(MsalBroadcastService));
            return fn();
        })
    ],
    declarations: [AppComponent, NewReleaseDialogComponent],
    bootstrap: [AppComponent]
})
export class AppModule {}
